import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { billingApi, type CheckoutSessionDetail } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  AuthShell,
  AUTH_FOOTNOTE_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_OUTLINE_BUTTON,
  AUTH_SUBTITLE_CLASS,
  AUTH_TITLE_CLASS,
} from '@/components/auth/AuthShell';

// ---------------------------------------------------------------------------
// OnboardingSuccessPage — confirmation surface after Stripe Checkout. Lands
// in the shared AuthShell (as SignupPage / LoginPage do, so the auth
// surfaces read as one moment), looks up the Stripe session by id
// to render a receipt block (plan name, seat count, masked email, short
// reference), and clearly tells the user to check their email for the
// password-set link.
//
// Three states:
//   1. session_id present + session is complete — full receipt
//   2. session_id present but Stripe says it isn't paid yet — soft
//      "still processing" state
//   3. session_id missing or lookup failed — generic confirmation
//      with no receipt (someone navigated direct to /onboarding/success)
// ---------------------------------------------------------------------------

/** "Already set up? Sign in": the outlined way back, at the main button's height. */
const SIGN_IN_BUTTON = cn(AUTH_OUTLINE_BUTTON, 'h-12 bg-foreground/5 text-base font-medium hover:bg-foreground/10');

export default function OnboardingSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') ?? '';

  const [session, setSession] = useState<CheckoutSessionDetail | null>(null);
  const [lookupState, setLookupState] = useState<'idle' | 'loading' | 'success' | 'failed' | 'no-id'>(
    sessionId ? 'loading' : 'no-id',
  );

  useEffect(() => {
    const prev = document.title;
    document.title = 'Welcome to Cadence';
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await billingApi.getCheckoutSession(sessionId);
        if (cancelled) return;
        setSession(detail);
        // status: "complete" means Stripe captured payment AND the
        // session is fully done; "open" / "expired" / null all read as
        // not-yet-complete. payment_status: "paid" or "no_payment_required"
        // (trial-only) are the success values.
        const isComplete =
          detail.status === 'complete' &&
          (detail.paymentStatus === 'paid' || detail.paymentStatus === 'no_payment_required');
        setLookupState(isComplete ? 'success' : 'failed');
      } catch {
        if (!cancelled) setLookupState('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Google Ads — "Trial Signup Completed" (the primary money event that
  // drives Smart Bidding). Fires exactly once per Stripe session, and only
  // after we've verified the session is actually complete/paid — never on a
  // direct navigation or an unpaid/abandoned session. The Stripe Checkout
  // Session ID is a stable, unique key, so it doubles as both the dedupe
  // guard (sessionStorage) and the conversion transaction_id (so a browser
  // refresh or Ads' own double-fire protection collapses to one conversion).
  useEffect(() => {
    if (lookupState !== 'success' || !sessionId) return;
    const dedupeKey = `gads_conv_${sessionId}`;
    try { if (sessionStorage.getItem(dedupeKey)) return; } catch { /* private mode — fall through */ }

    // Enhanced conversions: the success page only sees a masked email from
    // the API, so we recover the clear address stashed on the signup step
    // (same tab, survives the Stripe round-trip) and let gtag hash it.
    let stashedEmail: string | null = null;
    try { stashedEmail = sessionStorage.getItem('gads_ec_email'); } catch { /* ignore */ }
    if (stashedEmail) {
      window.gtag?.('set', 'user_data', { email: stashedEmail });
    }

    window.gtag?.('event', 'conversion', {
      send_to: 'AW-18076346956/B0jZCOzl_tscEMzUvKtD',
      value: 29.0,
      currency: 'GBP',
      transaction_id: sessionId,
    });

    try {
      sessionStorage.setItem(dedupeKey, '1');
      sessionStorage.removeItem('gads_ec_email');
    } catch { /* ignore */ }
  }, [lookupState, sessionId]);

  return (
    <AuthShell width="form">
      <div className="mb-5">
        <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
      </div>

      {lookupState === 'loading' && (
        <div className="flex h-[200px] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {lookupState === 'success' && session && (
        <>
          <CheckCircle2 className="mb-4 h-10 w-10 text-emerald-400" strokeWidth={1.5} />
          <h1 className={cn('mb-[10px]', AUTH_TITLE_CLASS)}>
            Welcome to Cadence.
          </h1>
          <p className={cn('mb-[30px]', AUTH_SUBTITLE_CLASS)}>
            Your trial has started. Check your inbox for your welcome email: its link signs you in and starts your setup.
          </p>

          {/* Receipt block — confirms what they bought + a short
              reference they can quote to support if the welcome
              email goes astray. */}
          <div className="mb-6 flex flex-col gap-3 rounded-[11px] border border-foreground/10 bg-foreground/5 p-5">
            {session.planName && (
              <Row label="Plan" value={session.planName} />
            )}
            {session.seatCount > 0 && (
              <Row label="Executive seats" value={`${session.seatCount}`} />
            )}
            {session.organisationName && (
              <Row label="Organisation" value={session.organisationName} />
            )}
            {session.maskedEmail && (
              <Row label="Welcome email sent to" value={session.maskedEmail} icon />
            )}
            <Row label="Reference" value={session.reference} mono />
          </div>

          <p className="mb-6 text-sm text-muted-foreground">
            The link in the welcome email is valid for 24 hours. If it expires before you click it, you can request a new one from the sign-in page.
          </p>

          <Button
            onClick={() => { window.location.href = '/login'; }}
            className={SIGN_IN_BUTTON}
            variant="outline"
          >
            Already set up? Sign in
          </Button>
        </>
      )}

      {(lookupState === 'failed' || lookupState === 'no-id') && (
        <>
          <Mail className="mb-4 h-10 w-10 text-primary" strokeWidth={1.5} />
          <h1 className={cn('mb-[10px]', AUTH_TITLE_CLASS)}>
            {lookupState === 'no-id' ? 'Welcome to Cadence.' : "We're still processing your payment."}
          </h1>
          <p className={cn('mb-[30px]', AUTH_SUBTITLE_CLASS)}>
            {lookupState === 'no-id'
              ? 'Check your inbox for your welcome email: its link signs you in and starts your setup.'
              : "Stripe is finishing up. You'll get a welcome email shortly with a link that signs you in and starts your setup. There's nothing else to do."}
          </p>
          <Button
            onClick={() => { window.location.href = '/login'; }}
            className={SIGN_IN_BUTTON}
            variant="outline"
          >
            Already set up? Sign in
          </Button>
        </>
      )}

      <p className={cn('mt-8 text-center', AUTH_FOOTNOTE_CLASS)}>
        Cadence is the product. Stratium is the company behind it.
      </p>
    </AuthShell>
  );
}

function Row({ label, value, icon, mono }: { label: string; value: string; icon?: boolean; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={AUTH_LABEL_CLASS}>{label}</span>
      <span
        className={cn(
          'text-right text-sm text-foreground/90',
          mono && 'font-mono text-xs tracking-[0.04em]',
          icon && 'flex items-center gap-1.5',
        )}
      >
        {icon && <Mail className="h-3.5 w-3.5 text-muted-foreground" />}
        {value}
      </span>
    </div>
  );
}
